import type { User } from '@paintscout/api'
import { removeAuth0Prefix } from '@paintscout/util'

/** Minimal shape of the owner snapshot denormalized onto contacts/quotes/deals/activities. */
interface OwnerLike {
  id?: string
  firstName?: string
  lastName?: string
}

/**
 * Returns the owner snapshot with `firstName`/`lastName` replaced by the live user's, keying the
 * users map prefix-less like the documents do.
 *
 * The snapshot is a point-in-time copy kept in sync only by the best-effort batch tasks `updateUser`
 * fires on a rename, so a renamed seat keeps rendering the previous person's name and initials
 * (#21800).
 *
 * Only the name is refreshed. `id`, `email` and `avatar` pass through untouched — deliberately, not
 * by oversight. `id` is already correct on a rename, and the avatar surfaces resolve the picture and
 * colour from the live user themselves. A stale `email` on a snapshot is a separate problem; nothing
 * here renders it.
 *
 * The snapshot is kept whenever the id is absent from the map or the live record has no name.
 * Deactivated and removed users are not returned by `searchUsers` and their historical attribution
 * is deliberate (see `deactivateUser`); customer-view is the same case, since its users query
 * returns nothing.
 *
 * Prefer this over reformatting `getOwnerDisplayName`'s output: splitting a formatted name back into
 * first and last would mangle multi-word first names.
 */
export function withLiveOwnerName<T extends OwnerLike>(owner: T, usersById?: Record<string, User>): T
export function withLiveOwnerName<T extends OwnerLike>(
  owner: T | undefined | null,
  usersById?: Record<string, User>
): T | undefined | null
export function withLiveOwnerName<T extends OwnerLike>(
  owner: T | undefined | null,
  usersById?: Record<string, User>
): T | undefined | null {
  if (!owner?.id) {
    return owner
  }

  const liveName = usersById?.[removeAuth0Prefix(owner.id)]?.user_metadata
  if (!liveName?.firstName && !liveName?.lastName) {
    return owner
  }

  return { ...owner, firstName: liveName.firstName, lastName: liveName.lastName }
}
